import { resourceGroupForCharacter } from '@minglun/domain';
import type { Character, FinalAttributeResult, ResourceGroup, Wheel, WheelResult } from '@minglun/domain';

export type ResultRankOrder = '默认' | '升序' | '降序';
export interface ResultWheelItem { entry: WheelResult; wheel: Wheel }
export interface ResultFacetValues {
  categoryIds: Set<string>;
  tagIds: Set<string>;
  partnerIds: Set<string>;
  partnerGroups: Set<ResourceGroup>;
}
export const resultComparisonChangeOptions = ['新增', '移除', '降低', '提升'] as const;
export type ResultComparisonVisibleChange = typeof resultComparisonChangeOptions[number];
export type ResultComparisonChange = ResultComparisonVisibleChange | '不变';
export interface ResultAttributeComparison {
  key: string;
  sourceAttributeId: string;
  valueKind: FinalAttributeResult['valueKind'];
  before: number;
  after: number;
  delta: number;
}

export function isActiveWheelResult(entry: WheelResult | undefined): entry is WheelResult {
  return Boolean(entry && (entry.rank > 0 || entry.star > 0));
}

export function classifyResultComparison(baseline: WheelResult | undefined, comparison: WheelResult | undefined): ResultComparisonChange {
  const left = isActiveWheelResult(baseline) ? baseline : undefined;
  const right = isActiveWheelResult(comparison) ? comparison : undefined;
  if (!left && !right) return '不变';
  if (!left) return '新增';
  if (!right) return '移除';
  if (left.rank === right.rank && left.star === right.star) return '不变';
  return right.rank > left.rank || (right.rank === left.rank && right.star > left.star) ? '提升' : '降低';
}

export function matchesResultComparisonChange(
  change: ResultComparisonChange,
  selected: readonly ResultComparisonVisibleChange[],
): boolean {
  if (change === '不变') return false;
  return selected.length === 0 || selected.includes(change);
}

export function compareResultAttributes(baseline: readonly FinalAttributeResult[], comparison: readonly FinalAttributeResult[]): ResultAttributeComparison[] {
  const key = (item: FinalAttributeResult): string => `${item.sourceAttributeId}\u0000${item.valueKind}`;
  const before = new Map(baseline.map((item) => [key(item), item]));
  const after = new Map(comparison.map((item) => [key(item), item]));
  return [...new Set([...before.keys(), ...after.keys()])].map((itemKey) => {
    const left = before.get(itemKey);
    const right = after.get(itemKey);
    const sourceAttributeId = (right ?? left)!.sourceAttributeId;
    const valueKind = (right ?? left)!.valueKind;
    const beforeValue = left?.value ?? 0;
    const afterValue = right?.value ?? 0;
    return { key: itemKey, sourceAttributeId, valueKind, before: beforeValue, after: afterValue, delta: afterValue - beforeValue };
  });
}

export function compareResultWheels(left: ResultWheelItem, right: ResultWheelItem, order: ResultRankOrder): number {
  if (order === '默认') return left.wheel.order - right.wheel.order;
  const direction = order === '升序' ? 1 : -1;
  return direction * (left.entry.rank - right.entry.rank || left.entry.star - right.entry.star) || left.wheel.order - right.wheel.order;
}

export function matchesResultMembers(wheel: Wheel, characters: Map<string, Character>, partnerFilter: string | readonly string[], partnerGroupFilter: string | readonly string[]): boolean {
  const partnerIds = Array.isArray(partnerFilter) ? partnerFilter : partnerFilter === '全部' ? [] : [partnerFilter];
  const partnerGroups = Array.isArray(partnerGroupFilter) ? partnerGroupFilter : partnerGroupFilter === '全部' ? [] : [partnerGroupFilter];
  if (partnerIds.length && !wheel.members.some((member) => partnerIds.includes(member.characterId))) return false;
  if (!partnerGroups.length) return true;
  return wheel.members.some((member) => {
    const character = characters.get(member.characterId);
    return character ? partnerGroups.includes(resourceGroupForCharacter(character) as ResourceGroup) : false;
  });
}

export function collectResultFacetValues(
  items: readonly ResultWheelItem[],
  characters: Map<string, Character>,
  tagsForWheel: (wheel: Wheel) => readonly string[],
): ResultFacetValues {
  const categoryIds = new Set<string>();
  const tagIds = new Set<string>();
  const partnerIds = new Set<string>();
  const partnerGroups = new Set<ResourceGroup>();
  for (const { wheel } of items) {
    categoryIds.add(wheel.wheelCategoryId);
    for (const tagId of tagsForWheel(wheel)) tagIds.add(tagId);
    for (const member of wheel.members) {
      const character = characters.get(member.characterId);
      if (!character) continue;
      partnerIds.add(character.id);
      partnerGroups.add(resourceGroupForCharacter(character));
    }
  }
  return { categoryIds, tagIds, partnerIds, partnerGroups };
}
